import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import {
  BackButton,
  Badge,
  EmptyState,
  PageHeader,
  SectionNav,
  Spinner,
  useSectionTab,
  type SectionDef,
} from "../ds";
import { CaptureSection } from "../components/device/CaptureSection";
import { FirewallSection } from "../components/device/FirewallSection";
import { InternetSection } from "../components/device/InternetSection";
import { LanSection } from "../components/device/LanSection";
import { LogsSection } from "../components/device/LogsSection";
import { OverviewSection } from "../components/device/OverviewSection";
import { SystemSection } from "../components/device/SystemSection";
import { WireguardSection } from "../components/device/WireguardSection";
import { NetworkSection } from "../components/device/NetworkSection";
import { WifiSection } from "../components/device/WifiSection";
import { isUplink } from "../lib/roles";
import {
  api,
  errorText,
  type Device,
  type DeviceDetail,
  type LanClient,
  type NetworkStatus,
  type SectionSnapshot,
  type WifiStatus,
} from "../lib/api";
import { SectionSink } from "../lib/hooks/useSection";
import { useFleetEvents } from "../lib/hooks/useFleetEvents";
import { relTime } from "../lib/format";

// Detailseite eines Routers — das zentrale „self-built LuCI“.
//
// Datensparsamkeit (docs/device-management.md): gezeigt wird der Server-Cache;
// nachgeholt wird nur für die SICHTBARE Sektion, nur wenn der Router online und
// der Stand älter als 5 Minuten ist (`useSection`). Kein Timer, kein Polling,
// nichts für Tabs, die gerade niemand ansieht.

// Die Tabs folgen den Fragen, die jemand an einen Router hat — nicht den
// uci-Configs. Vorher beantworteten vier Tabs (LTE/WAN, Netzwerk, WLAN,
// WireGuard) gemeinsam keine einzige Frage ganz: „wie kommt die Box raus?"
// stand nirgends vollständig. Jetzt hat jede Frage genau eine Adresse.
//
// WLAN ist dabei keine Einheit: ein AP versorgt lokal, ein STA IST die Leitung.
// Die Trennlinie läuft deshalb entlang der Rolle — der STA erscheint als Uplink
// unter „Internet", editiert wird er (samt Radio) unter „WLAN". Ein Ort pro
// Sache, zwei Sichten darauf.
type TabId =
  | "overview"
  | "internet"
  | "wifi"
  | "lan"
  | "firewall"
  | "wireguard"
  | "system"
  | "logs"
  | "capture";

/** Welche Agent-Capability eine Sektion braucht (Feature-Detection aus `hello`).
 *
 *  Fast alles liest über `state_get`: der Agent liefert nur noch Rohstand, die
 *  Sektion entsteht auf dem Server (docs/device-management.md). Ein Router mit
 *  altem Agent meldet `state_get` nicht — dann bleiben genau die Tabs übrig,
 *  die er wirklich kann. */
const REQUIRED_CAPABILITY: Record<TabId, string> = {
  overview: "state_get",
  internet: "state_get",
  wifi: "state_get",
  lan: "state_get",
  firewall: "state_get",
  wireguard: "state_get",
  system: "state_get",
  logs: "logs_pull",
  // Eigener Tab statt Karte unter „Logs": ein Mitschnitt ist keine Anzeige,
  // sondern ein Vorgang mit Ergebnisdatei. Er erscheint nur auf Routern, deren
  // Image tcpdump mitbringt — der Agent meldet die Capability sonst nicht.
  capture: "capture_start",
};

const ALL_SECTIONS: SectionDef<TabId>[] = [
  { id: "overview", label: "Übersicht", iconName: "dashboard" },
  { id: "internet", label: "Internet", iconName: "swap" },
  { id: "wifi", label: "WLAN", iconName: "wifi" },
  { id: "lan", label: "LAN", iconName: "users" },
  { id: "firewall", label: "Firewall", iconName: "shield" },
  // „Fernzugriff", nicht „WireGuard": der Tunnel ist kein Uplink, er läuft
  // über einen. Der Name sagt die Rolle — wie WIR reinkommen, nicht wie die
  // Box rausgeht.
  { id: "wireguard", label: "Fernzugriff", iconName: "key" },
  { id: "system", label: "System", iconName: "gear" },
  { id: "logs", label: "Logs", iconName: "log" },
  { id: "capture", label: "Mitschnitt", iconName: "activity" },
];

/** Zustand für die Tab-Leiste — ausschließlich aus dem, was ohnehin im Cache
 *  liegt. Kein Abruf: eine Leiste, die den Router weckt, wäre die teuerste
 *  Anzeige der Oberfläche (docs/device-management.md → Datensparsamkeit). */
function withLiveState(sections: SectionDef<TabId>[], device: DeviceDetail): SectionDef<TabId>[] {
  const network = device.sections.network?.data as NetworkStatus | undefined;
  const wifi = device.sections.wifi?.data as WifiStatus | undefined;
  const lan = device.sections.lan?.data as { clients?: LanClient[] } | undefined;

  const uplinks = network?.interfaces?.filter(isUplink);
  const ssids = wifi?.radios?.flatMap((r) => (r.networks ?? []).filter((n) => !n.disabled));

  return sections.map((s) => {
    if (s.id === "internet" && uplinks) {
      const up = uplinks.filter((i) => i.up).length;
      return { ...s, count: up, tone: up > 0 ? "success" : "danger" };
    }
    if (s.id === "wifi" && ssids) return { ...s, count: ssids.length };
    if (s.id === "lan" && lan?.clients) return { ...s, count: lan.clients.length };
    return s;
  });
}

function DeviceBody({ tab, device }: { tab: TabId; device: DeviceDetail }) {
  switch (tab) {
    case "overview":
      return <OverviewSection device={device} />;
    case "internet":
      return <InternetSection device={device} />;
    case "wifi":
      return <WifiSection device={device} />;
    // Clients und der Interface-Editor. Der Editor bleibt der EINE Ort, an dem
    // ein Interface geändert wird — auch die Uplinks. „Internet" ist die
    // Rollensicht darauf, keine zweite Bearbeitung.
    case "lan":
      return (
        <div className="flex flex-col gap-5">
          <LanSection device={device} />
          <NetworkSection device={device} />
        </div>
      );
    case "firewall":
      return <FirewallSection device={device} />;
    case "wireguard":
      return <WireguardSection device={device} />;
    case "system":
      return <SystemSection device={device} />;
    case "logs":
      return <LogsSection device={device} />;
    case "capture":
      return <CaptureSection device={device} />;
  }
}

export function DevicePage() {
  const { deviceId = "" } = useParams<{ deviceId: string }>();
  const [device, setDevice] = useState<DeviceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Nur der gecachte Detailstand — weckt den Router nicht.
  const load = useCallback(() => {
    api
      .getDevice(deviceId)
      .then((d) => {
        setDevice(d);
        setError(null);
      })
      .catch((e: unknown) => setError(errorText(e)));
  }, [deviceId]);

  useEffect(load, [load]);

  // Kopf live: online/last-seen/firmware pusht der Fleet-Stream für genau diesen
  // Router. Sektionen und metrics bleiben Router-Fetch (kein Frame je Heartbeat).
  const onDevice = useCallback(
    (row: Device) => {
      if (row.id !== deviceId) return;
      setDevice((d) => (d ? { ...d, ...row } : d));
    },
    [deviceId],
  );
  useFleetEvents({ onDevice });

  // Was eine Sektion frisch geholt hat, gehört in den Seitenzustand: sonst
  // vergisst ein Tabwechsel den Stand und der Router zahlt ihn erneut in
  // Funkverkehr (docs/device-management.md → „Nachladen beim Öffnen“).
  const keepSnapshot = useCallback((section: string, snapshot: SectionSnapshot) => {
    setDevice((d) => (d ? { ...d, sections: { ...d.sections, [section]: snapshot } } : d));
  }, []);

  const sections = useMemo(() => {
    const caps = device?.capabilities ?? [];
    const available =
      caps.length === 0 // Agent meldet nichts → alles zeigen
        ? ALL_SECTIONS
        : ALL_SECTIONS.filter((s) => caps.includes(REQUIRED_CAPABILITY[s.id]));
    return device ? withLiveState(available, device) : available;
  }, [device]);

  const { active, setActive } = useSectionTab(sections, "overview");

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <BackButton href="/fleet" label="Zurück zur Fleet" />
        <EmptyState iconName="close" title="Router nicht ladbar" description={error} />
      </div>
    );
  }

  if (!device) {
    return (
      <div className="flex justify-center p-16">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <BackButton href="/fleet" label="Zurück zur Fleet" />

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {device.hostname ?? device.id}
            <Badge variant={device.online ? "success" : "neutral"} dot>
              {device.online ? "online" : "offline"}
            </Badge>
          </span>
        }
        subtitle={`${device.model ?? "unbekanntes Modell"} · ${device.id} · zuletzt gesehen ${relTime(
          device.lastSeen,
        )}`}
      />

      <SectionNav sections={sections} value={active} onChange={setActive} />

      <SectionSink.Provider value={keepSnapshot}>
        <DeviceBody tab={active} device={device} />
      </SectionSink.Provider>
    </div>
  );
}
